import { MESSAGE_TYPES } from 'hooks/websocket/constants'
import type { WebSocketData } from 'hooks/websocket/types'
import { buildTranslate } from 'locales'
import { setIsLoading } from 'slices/aiConversations'
import type {
  OperatorOption,
  PickerData,
  PickerDetailsSource,
  PickerLevelConfig,
  PickerSelection,
  PickerSource,
  PickerVariantsLookup,
  SelectionType,
} from 'slices/aiConversations/pickerTypes'
import type { Message } from 'slices/aiConversations/types'
import * as uuid from 'uuid'

import type { MessageHandler, MessageHandlerContext } from './types'

const t = buildTranslate('components.ai.picker')

/** One of a level's sources; the backend camelCases its keys and passes `params` and `variantsLookup` through as is */
interface RawPickerSource {
  name: string
  selectionType: SelectionType
  keyField?: string
  valueField?: string
  endpoint: string
  params?: Record<string, unknown>
  variantsField?: string
  variantsLookup?: PickerVariantsLookup
  // { table_name, fields }, passed through from picker_configurations.yml
  detailsSource?: unknown
}

/**
 * Raw picker config as received from the backend.
 * Backend now sends camelCase keys after transformation in picker_payload_builder.rb
 */
interface RawPickerLevelConfig {
  id: string
  label: string
  // null on a level that reads from its sources
  selectionType: SelectionType | null
  operatorOptions?: OperatorOption[]
  endpoint: string | null
  // Params can include various types for snowflake query support
  params?: Record<string, string | string[] | boolean | number>
  operator?: string
  subtitle?: string
  sources?: RawPickerSource[]
  defaultSource?: string
}

// Canonical ids arrive as JSON numbers
type RawPickerId = string | number

interface RawPickerSelection {
  level_1: RawPickerId | RawPickerId[]
  level_1_label?: string
  operator?: string
  level_2?: RawPickerId[]
  level_2_labels?: string[]
}

export interface RawPickerConfig {
  picker_id: string
  type: string
  title: string
  subtitle?: string
  level_1: RawPickerLevelConfig
  level_2?: RawPickerLevelConfig
  selections: RawPickerSelection[]
}

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === 'string')

/** A details source the payload names, or null when it names none or a malformed one */
function detailsSourceOf(raw: unknown): PickerDetailsSource | null {
  if (typeof raw !== 'object' || raw === null) return null
  const tableName = Object.getOwnPropertyDescriptor(raw, 'table_name')?.value
  const fields = Object.getOwnPropertyDescriptor(raw, 'fields')?.value
  if (typeof tableName !== 'string' || !tableName || !isStringArray(fields)) {
    return null
  }
  return { tableName, fields }
}

function transformSource(raw: RawPickerSource): PickerSource {
  return {
    name: raw.name,
    selectionType: raw.selectionType,
    keyField: raw.keyField ?? 'id',
    valueField: raw.valueField ?? 'name',
    endpoint: raw.endpoint,
    params: raw.params ?? {},
    variantsField: raw.variantsField ?? null,
    variantsLookup: raw.variantsLookup ?? null,
    detailsSource: detailsSourceOf(raw.detailsSource),
  }
}

/**
 * Transform raw config to frontend PickerLevelConfig.
 * Backend now sends most keys in camelCase, so minimal transformation needed.
 */
function transformLevelConfig(raw: RawPickerLevelConfig): PickerLevelConfig {
  return {
    id: raw.id,
    label: raw.label,
    selectionType: raw.selectionType,
    operatorOptions: raw.operatorOptions,
    endpoint: raw.endpoint,
    params: raw.params,
    operator: raw.operator,
    subtitle: raw.subtitle,
    sources: raw.sources?.map(transformSource),
    defaultSource: raw.defaultSource,
  }
}

function transformSelection(raw: RawPickerSelection): PickerSelection {
  return {
    level1: Array.isArray(raw.level_1)
      ? raw.level_1.map(String)
      : String(raw.level_1),
    level1Label: raw.level_1_label,
    operator: raw.operator,
    level2: raw.level_2?.map(String),
    level2Labels: raw.level_2_labels,
  }
}

function parsePickerConfig(config: RawPickerConfig): PickerData {
  return {
    pickerId: config.picker_id,
    type: config.type,
    title: config.title,
    subtitle: config.subtitle,
    level1: transformLevelConfig(config.level_1),
    level2: config.level_2 ? transformLevelConfig(config.level_2) : undefined,
    selections: config.selections.map(transformSelection),
  }
}

/**
 * Clears any streaming tool call messages before showing picker.
 */
function clearToolCalls(context: MessageHandlerContext) {
  const state = context.getState()
  const currentStreamingMessage =
    state.aiConversations?.webSocket?.currentStreamingMessage
  if (currentStreamingMessage?.isToolCall) {
    context.setCurrentStreamingMessage(null)
  }
}

/**
 * Handler for PICKER messages from the AI backend.
 * Parses the picker configuration and creates a message with pickerData
 * that the ThanxAIPicker component will render.
 */
export const pickerHandler: MessageHandler = {
  type: MESSAGE_TYPES.PICKER,
  handle: (message: WebSocketData, context: MessageHandlerContext) => {
    context.clearStreamingTimeout()
    clearToolCalls(context)

    try {
      if (!message.content) {
        throw new Error('No picker content')
      }

      const rawConfig: RawPickerConfig = JSON.parse(message.content)
      const pickerData = parsePickerConfig(rawConfig)

      const pickerMessage: Message = {
        id: uuid.v4(),
        content: '',
        type: 'ai',
        timestamp: new Date(),
        isMarkdown: false,
        sequenceNumber:
          message.sequenceNumber ?? ++context.sequenceCounter.current,
        serverId: message.serverId,
        clientId: uuid.v4(),
        isOptimistic: false,
        pickerData,
        pickerSubmitted: false,
      }

      context.addMessageWithOrdering(pickerMessage)
      context.dispatch(setIsLoading(false))
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[PickerHandler] Failed to parse picker:', error)

      const errorMessage: Message = {
        id: uuid.v4(),
        content: t('parse_error') || 'Error loading picker',
        type: 'ai',
        timestamp: new Date(),
        isMarkdown: false,
        sequenceNumber:
          message.sequenceNumber ?? ++context.sequenceCounter.current,
        serverId: message.serverId,
        clientId: uuid.v4(),
        isOptimistic: false,
      }

      context.addMessageWithOrdering(errorMessage)
      context.dispatch(setIsLoading(false))
    }
  },
}
